import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";

// API
import fetchCitiesAsync from "./api/fetchCitiesAsync";
import fetchDistrictsAsync from "./api/fetchDistrictsAsync";
import fetchStoresAsync from "./api/fetchStoresAsync";

import isMeasurement from "../../utils/isMeasurement";

const useStyles = makeStyles((theme) => ({
  spinnerProgress: {
    position: "absolute",
    top: "50%",
    left: "90%",
    marginTop: -5,
    marginLeft: -5,
  },
}));

export default function AddressPage({ fields, errors, handleChange }) {
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  const [loadingCities, setLoadingCities] = React.useState(false);
  const [loadingDistricts, setLoadingDistricts] = React.useState(false);
  const [loadingStores, setLoadingStores] = React.useState(false);
  const [showNoServicesError, setShowNoServicesError] = useState(false);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [stores, setStores] = useState([]);

  // componentDidMount
  useEffect(() => {
    fetchCities();

    if (fields.city) {
      if (isMeasurement(fields.service)) {
        fetchDistricts(fields.city.Id, fields.service.ServiceId);
      }

      fetchStores(fields.city.Id, fields.service.Service_Type, fields.service.ServiceId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCities = () => {
    if (!loadingCities) {
      setLoadingCities(true);

      fetchCitiesAsync().then((cities) => {
        setCities(cities);
        setLoadingCities(false);
      });
    }
  };

  const fetchDistricts = (cityId, serviceId) => {
    if (!loadingDistricts) {
      setLoadingDistricts(true);

      fetchDistrictsAsync(cityId, serviceId).then((districts) => {
        setDistricts(districts);
        setLoadingDistricts(false);
      });
    }
  };

  const fetchStores = (cityId, serviceType, serviceId) => {
    if (!loadingStores) {
      setLoadingStores(true);

      fetchStoresAsync(cityId, serviceType, serviceId).then((stores) => {
        setStores(stores);
        setLoadingStores(false);

        // if no stores, show an error message
        stores.length === 0 ? setShowNoServicesError(true) : setShowNoServicesError(false);

        // if in measurement and only one store found, select it automatically
        if (isMeasurement(fields.service) && stores.length === 1) {
          handleChange("store")(stores[0]);
        }
      });
    }
  };

  const handleCityChange = (e, city) => {
    // get object and store in state
    //const city = cities.find((city) => city.Id === e.target.value);
    handleChange("city")(city);

    // reset district when city is changed (don't run validation on district doValidation: false)
    handleChange("district", false)("");
    setDistricts([]);

    // reset store when city is changed
    handleChange("store", false)("");
    setStores([]);

    // load districts for selected city, if any
    if (city) {
      if (isMeasurement(fields.service)) {
        fetchDistricts(city.Id, fields.service.ServiceId);
      }

      fetchStores(city.Id, fields.service.Service_Type, fields.service.ServiceId);
    }
  };

  const handleDistrictChange = (e, district) => {
    // get object
    // const district = districts.find(
    //   (district) => district.Id === e.target.value
    // );

    // store in state
    handleChange("district")(district);
  };

  const handleStoreChange = (e) => {
    // get object
    const store = stores.find((store) => store.Id === e.target.value);

    // store in state
    handleChange("store")(store);
  };

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        {t("Select your city and district")}:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors["city"] || showNoServicesError}>
            <Autocomplete
              options={["", ...cities]}
              getOptionLabel={(city) => {
                let result = i18n.language === "ar" ? city.LocalizedName : city.Name;
                return result === undefined ? "" : result;
              }}
              id="city-select"
              value={fields.city}
              onChange={handleCityChange}
              renderInput={(params) => (
                <TextField {...params} label={t("City")} error={!!errors["city"]} margin="normal" />
              )}
            />
            {loadingCities && <CircularProgress size={24} className={classes.spinnerProgress} />}
            {!!errors["city"] && <FormHelperText>{errors["city"]}</FormHelperText>}
            {showNoServicesError && (
              <FormHelperText>{t("Sorry, selected service is not available for your city.")}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {isMeasurement(fields.service) && (
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors["district"]}>
              <Autocomplete
                options={["", ...districts]}
                getOptionLabel={(district) => {
                  let result = i18n.language === "ar" ? district.LocalizedName : district.Name;
                  return result === undefined ? "" : result;
                }}
                id="district-select"
                value={fields.district}
                onChange={handleDistrictChange}
                renderInput={(params) => (
                  <TextField {...params} error={!!errors["district"]} label={t("District")} margin="normal" />
                )}
              />
              {loadingDistricts && <CircularProgress size={24} className={classes.spinnerProgress} />}
              <FormHelperText>{errors["district"]}</FormHelperText>
            </FormControl>
          </Grid>
        )}

        {isMeasurement(fields.service) ? ( // measurement only
          stores.length > 1 && ( // don't show store dropdown only one store provided
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors["store"]}>
                <InputLabel id="store-select-label">{t("Store")}</InputLabel>
                <Select
                  labelId="store-select-label"
                  label={t("Store")}
                  id="store-select"
                  value={fields.store ? fields.store.Id : ""}
                  onChange={handleStoreChange}
                >
                  {stores &&
                    stores.map((store) => (
                      <MenuItem key={store.Id} value={store.Id}>
                        {i18n.language === "ar" ? store.LocalizedName : store.Name}
                      </MenuItem>
                    ))}
                </Select>
                {loadingStores && <CircularProgress size={24} className={classes.spinnerProgress} />}
                <FormHelperText>{errors["store"]}</FormHelperText>
              </FormControl>
            </Grid>
          )
        ) : (
          // for planning, always show store dropdown
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors["store"]}>
              <InputLabel id="store-select-label">{t("Store")}</InputLabel>
              <Select
                labelId="store-select-label"
                label={t("Store")}
                id="store-select"
                value={fields.store ? fields.store.Id : ""}
                onChange={handleStoreChange}
              >
                {stores &&
                  stores.map((store) => (
                    <MenuItem key={store.Id} value={store.Id}>
                      {i18n.language === "ar" ? store.LocalizedName : store.Name}
                    </MenuItem>
                  ))}
              </Select>
              {loadingStores && <CircularProgress size={24} className={classes.spinnerProgress} />}
              <FormHelperText>{errors["store"]}</FormHelperText>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </div>
  );
}
